import { useEffect, useRef, useState } from "react";
import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";

// An ordinary send is not worth narrating: it round-trips before anyone could
// read a status line, and the send button's own busy state already covers
// "sending". This exists for the exceptional cases only — a background
// agent or teammate holding the runtime reports a genuine reason to wait (an
// immediate onWaiting call), or the request has run long enough with no
// reason given that something is actually stuck. Neither applies to a
// normal send, so nothing is shown until one of them does.
export const STUCK_REQUEST_DELAY_MS = 4000;

export interface RuntimeAdmissionState {
  readonly startedAt: string;
  // A server-reported reason to wait (e.g. runtime busy with another turn),
  // or null while nothing has explained the wait yet.
  readonly message: string | null;
  readonly onCancel: () => void;
}

type AdmissionSnapshot = Omit<RuntimeAdmissionState, "startedAt">;

export function useRuntimeAdmission(environmentId: EnvironmentId, threadId: ThreadId | undefined) {
  const pending = useRef<AbortController | null>(null);
  const [runtimeAdmission, setRuntimeAdmission] = useState<
    | (RuntimeAdmissionState & {
        readonly environmentId: EnvironmentId;
        readonly threadId: ThreadId | undefined;
      })
    | null
  >(null);
  useEffect(() => () => pending.current?.abort(new Error("Launch cancelled.")), []);

  const begin = (startedAt: string) => {
    const controller = new AbortController();
    pending.current = controller;
    const onAbort = () =>
      controller.abort(new Error("Launch cancelled. Your message is ready to edit or resend."));

    let revealed = false;
    let snapshot: AdmissionSnapshot = { message: null, onCancel: onAbort };
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

    const clearFallbackTimer = () => {
      if (fallbackTimer === undefined) return;
      clearTimeout(fallbackTimer);
      fallbackTimer = undefined;
    };
    const publish = () => {
      if (controller.signal.aborted) return;
      setRuntimeAdmission({ environmentId, threadId, startedAt, ...snapshot });
    };
    const reveal = () => {
      clearFallbackTimer();
      if (revealed) return;
      revealed = true;
      publish();
    };
    const armFallbackTimer = () => {
      clearFallbackTimer();
      fallbackTimer = setTimeout(reveal, STUCK_REQUEST_DELAY_MS);
    };
    const update = (next: AdmissionSnapshot) => {
      snapshot = next;
      if (revealed) publish();
    };

    armFallbackTimer();

    return {
      signal: controller.signal,
      onWaiting: (message: string) => {
        if (pending.current === controller && !controller.signal.aborted) {
          update({ message, onCancel: onAbort });
          reveal();
        }
      },
      // Once the start request is actually in flight, cancelling locally is
      // no longer enough — the server may already have the message, so
      // Cancel needs to become a real interrupt. This has no visual effect
      // of its own (no "Sending message" chrome — the send button's busy
      // state already covers that); it only swaps what Cancel does, and
      // keeps the fallback timer armed so a dispatch that hangs still
      // surfaces Cancel after STUCK_REQUEST_DELAY_MS.
      useServerInterrupt: (interrupt: () => Promise<void>) => {
        controller.signal.throwIfAborted();
        pending.current = null;
        update({
          message: snapshot.message,
          onCancel: () => {
            void interrupt().catch((error: unknown) => {
              setRuntimeAdmission((current) =>
                current?.environmentId === environmentId && current.threadId === threadId
                  ? {
                      ...current,
                      message:
                        error instanceof Error ? error.message : "Could not cancel. Try again.",
                    }
                  : current,
              );
            });
          },
        });
        if (!revealed) armFallbackTimer();
      },
      // Always safe to call once the request has settled (accepted, failed,
      // or cancelled): stops a stale fallback timer from firing later and
      // reviving a card nothing still needs.
      settle: () => {
        clearFallbackTimer();
      },
      finish: () => {
        clearFallbackTimer();
        if (pending.current === controller) pending.current = null;
        setRuntimeAdmission(null);
      },
    };
  };
  return {
    runtimeAdmission:
      runtimeAdmission?.environmentId === environmentId && runtimeAdmission.threadId === threadId
        ? runtimeAdmission
        : null,
    begin,
  };
}
